import { useMemo } from 'react';
import { DEFAULT_CURRENT_PAGE, DEFAULT_ROWS_PER_PAGE } from '../../helpers/consts';
import { SORT_ORDER } from '../../helpers/enums';
import type { GetWorkspacesRequest } from '../../lib/api/workspaces/dto';
import { useAdvertisers } from '../../modules/GenomeWorkspaceView/LeftView/hooks/useAdvertisers';
import { useGetUsersWorkspacesQuery } from '../api/useWorkspaces';
import { usePermissions } from '../usePermissions';
import { useWorkspaceTypes } from './useWorkspacesTypes';

interface Params {
  accountID?: number;
  advertiserIDs?: number[];
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: SORT_ORDER;
  searchPhrase?: string;
  workspaceTypeIDs?: number[];
  createdBy?: string[];
  accessAdvertiserIDs?: number[];
}

export const useWorkspacesListApi = ({
  accountID: rawAccountID,
  page = DEFAULT_CURRENT_PAGE,
  limit = DEFAULT_ROWS_PER_PAGE,
  advertiserIDs,
  searchPhrase = '',
  sortBy,
  sortOrder,
  workspaceTypeIDs,
  createdBy,
  accessAdvertiserIDs,
}: Params) => {
  const accountID = rawAccountID !== undefined ? Number(rawAccountID) : 0;
  const { email: permissionEmail } = usePermissions();
  const { workspaceTypes } = useWorkspaceTypes();
  const { advertisers, isAdvertisersDataLoading } = useAdvertisers();

  const defaultWorkspaceTypeIDs = useMemo(() => workspaceTypes.map(({ type_id }) => type_id), [workspaceTypes]);

  const queryParams: GetWorkspacesRequest = {
    accountID,
    workspaceTypeID: workspaceTypeIDs?.length ? workspaceTypeIDs : defaultWorkspaceTypeIDs,
    searchTerm: searchPhrase || undefined,
    advertiserIDs: advertiserIDs?.length ? advertiserIDs : undefined,
    createdBy: createdBy?.length ? createdBy : undefined,
    paginationPage: Math.max(0, page - 1),
    paginationLimit: limit,
    paginationEnabled: true,
    paginationSortBy: [
      sortBy === 'workspace_advertiser_id' ? 'workspace_advertiser_name' : sortBy || 'workspace_created_at',
    ],
    userEmail: permissionEmail,
    paginationSortDirection: sortOrder || SORT_ORDER.DESC,
    accessAdvertiserIDs,
  };

  const enabled = !!(accountID && permissionEmail && queryParams.workspaceTypeID && !isAdvertisersDataLoading);

  const { data, isLoading, error, isError, isFetching, refetch } = useGetUsersWorkspacesQuery(
    {
      ...queryParams,
      advertiserMapping: advertisers
        .filter((advertiser) => (data?.usedAdvertisers ?? []).includes(advertiser.id))
        .map(({ id, value }) => ({ advId: id, advName: value })),
    },
    { enabled },
  );

  const allWorkspaces = useMemo(() => data?.data ?? data?.items ?? [], [data?.data, data?.items]);

  const areWorkspacesLoading = useMemo(
    () => (!data && !isError) || (isLoading && !isError),
    [data, isLoading, isError],
  );

  const workspacesCount = data?.totalItemCount ?? 0;
  const workspacesPages = data?.totalPageCount ?? 1;

  return {
    allWorkspaces,
    userWorkspaces: allWorkspaces,
    areWorkspacesLoading,
    areWorkspacesRefetching: isFetching,
    isWorkspaceError: isError,
    workspaceError: error,
    workspaceRefetch: refetch,
    workspacesPages,
    workspacesCount,
    usedCreatedBy: data?.usedCreatedBy ?? [],
    usedAdvertisers: data?.usedAdvertisers ?? [],
  };
};
